import { $ZodError } from "zod/v4/core";
import { z, flattenError } from "zod/v4-mini";
import type { Logger } from "@/utils/logger";

const ActionRegistryCollectionPayloadSchema = z.object({
  action: z.string(),
  channelActionId: z.string(),
});

const ActionRegistryBulkPayloadSchema = z.record(z.string(), z.string());

const ActionRegistryPayloadSchema = z.pipe(
  z.union([
    ActionRegistryCollectionPayloadSchema,
    z.array(ActionRegistryCollectionPayloadSchema),
    ActionRegistryBulkPayloadSchema,
  ]),
  z.transform(
    (payload): z.infer<typeof ActionRegistryCollectionPayloadSchema>[] => {
      if (Array.isArray(payload)) {
        return payload as z.infer<
          typeof ActionRegistryCollectionPayloadSchema
        >[];
      } else if (
        ActionRegistryCollectionPayloadSchema.safeParse(payload).success
      ) {
        return [payload] as z.infer<
          typeof ActionRegistryCollectionPayloadSchema
        >[];
      }

      return Object.entries(payload).map(([action, channelActionId]) => ({
        action,
        channelActionId,
      })) as z.infer<typeof ActionRegistryCollectionPayloadSchema>[];
    },
  ),
);

export type SDKActionRegistryPayload = {
  action: string;
  channelActionId: string;
};

export class SDKActionRegistry {
  #logger: Logger;
  #actionMap: Record<string, string>;

  constructor(logger: Logger, actionMap?: Record<string, string>) {
    this.#logger = logger.extend(this.constructor.name);
    this.#actionMap = actionMap ?? {};
  }

  public registerActionMapping(payload: unknown) {
    try {
      const parsedPayload = ActionRegistryPayloadSchema.parse(payload);

      for (const item of parsedPayload) {
        this.#actionMap[item.action] = item.channelActionId;
      }
    } catch (error) {
      if (error instanceof $ZodError) {
        this.#logger.error(
          "Error registering action",
          flattenError(error).fieldErrors,
        );
        return;
      }
      this.#logger.error("Error registering action", { error });
    }
  }

  public getChannelActionId(action: string): string | undefined {
    return this.#actionMap[action];
  }
}
